"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UpsAdminClient = void 0;
const applications_1 = require("./commands/applications");
const SearchVariantsCommand_1 = require("./commands/variants/SearchVariantsCommand");
const CreateAndroidVariantCommand_1 = require("./commands/variants/create/CreateAndroidVariantCommand");
const DeleteVariantCommand_1 = require("./commands/variants/DeleteVariantCommand");
const RenewSecretCommand_1 = require("./commands/variants/RenewSecretCommand");
const UpdateAndroidVariantCommand_1 = require("./commands/variants/update/UpdateAndroidVariantCommand");
const CreateIOSCertVariantCommand_1 = require("./commands/variants/create/CreateIOSCertVariantCommand");
const CreateIOSTokenVariantCommand_1 = require("./commands/variants/create/CreateIOSTokenVariantCommand");
const CreateWebPushVariantCommand_1 = require("./commands/variants/create/CreateWebPushVariantCommand");
const UpdateIOSCertVariantCommand_1 = require("./commands/variants/update/UpdateIOSCertVariantCommand");
const UpdateIOSTokenVariantCommand_1 = require("./commands/variants/update/UpdateIOSTokenVariantCommand");
const UpdateWebPushVariantCommand_1 = require("./commands/variants/update/UpdateWebPushVariantCommand");
const ApiClient_1 = require("./commands/ApiClient");
const RenewMasterSecretCommand_1 = require("./commands/applications/RenewMasterSecretCommand");
const LoadMetricsCommand_1 = require("./commands/metrics/LoadMetricsCommand");
const GetConfigCommand_1 = require("./commands/auth/GetConfigCommand");
/**
 * This class is to be used to access the UPS admin function from typescript.
 * The functions are divided into two main branches:
 * * applications: contains all the functions that relate to applications
 * * variants: contains all the function that relates to variants
 *
 * The variant branch is itself divided into other branches, one for each of the supported platforms
 * (android, iOS, iOS_Token, webpush).
 */
class UpsAdminClient {
    constructor(serverURL, credentials) {
        /** Contains all the method related to application */
        this.applications = {
            /**
             * Search for applications that match a specified filter.
             * To configure the filter, the fluent interface must be used:
             * ```typescript
             * const upsClient = new UpsAdminClient('http://myups:9999');
             * const searchResult = await upsClient.applications.search()
             *  .withName('myAppName')
             *  .execute();
             * ```
             * If no filter is specified, all applications are returned.
             *
             * **WARNING**: the result is paginated. use `.withPageSize` and `.page` to get the other pages.
             */
            search: () => new applications_1.SearchApplicationsCommand(this.api),
            /**
             * Creates a new application. The only required parameter is the `application name`, however
             * other application details can be set through the usage of the fluid interface.
             * Example:
             * ```typescript
             * const upsClient = new UpsAdminClient('http://myups:9999');
             * const app = await upsClient.applications.create('mynewapp')
             *  .withDescription('Test application')
             *  .execute();
             * ```
             * @param name
             */
            create: (name) => new applications_1.CreateApplicationCommand(this.api, name),
            /**
             * Updates application details. All the updatable fields can be set using the provided
             * fluid interface.
             * Example:
             * ```typescript
             * const upsClient = new UpsAdminClient('http://myups:9999');
             * await upsClient.applications.update('my-app-id')
             *  .withName('new name')
             *  .execute();
             * ```
             * @param appId the `pushApplicationID` of the app to be updated
             */
            update: (appId) => new applications_1.UpdateApplicationsCommand(this.api, appId),
            /**
             * Deletes a set of push application.
             * Which application will be deleted is decided by applying the specified filter.
             *
             * **Warning**: specifying no filter means that all the applications will be deleted.
             * Example:
             * ```typescript
             * const upsClient = new UpsAdminClient('http://myups:9999');
             * await upsClient.applications.delete('my-app-id')
             *  .withName('name-of-the-app-to-delete')
             *  .execute();
             * ```
             */
            delete: () => new applications_1.DeleteApplicationsCommand(this.api),
            metrics: (appId) => new LoadMetricsCommand_1.LoadMetricsCommand(this.api, appId),
            renewSecret: (appId) => new RenewMasterSecretCommand_1.RenewMasterSecretCommand(this.api, appId),
        };
        this.variants = {
            search: (appId) => new SearchVariantsCommand_1.SearchVariantsCommand(this.api, appId),
            delete: (appId) => new DeleteVariantCommand_1.DeleteVariantCommand(this.api, appId),
            android: {
                create: (appId) => new CreateAndroidVariantCommand_1.CreateAndroidVariantCommand(this.api, appId),
                update: (appId, variantId) => new UpdateAndroidVariantCommand_1.UpdateAndroidVariantCommand(this.api, appId, variantId),
                renewSecret: (appId, variantId) => new RenewSecretCommand_1.RenewSecretCommand(this.api, appId, 'android', variantId),
                search: (appId) => new SearchVariantsCommand_1.SearchVariantsCommand(this.api, appId).withType('android'),
                delete: (appId) => new DeleteVariantCommand_1.DeleteVariantCommand(this.api, appId).withType('android'),
            },
            ios: {
                create: (appId) => new CreateIOSCertVariantCommand_1.CreateIOSCertVariantCommand(this.api, appId),
                update: (appId, variantId) => new UpdateIOSCertVariantCommand_1.UpdateIOSCertVariantCommand(this.api, appId, variantId),
                renewSecret: (appId, variantId) => new RenewSecretCommand_1.RenewSecretCommand(this.api, appId, 'ios', variantId),
                search: (appId) => new SearchVariantsCommand_1.SearchVariantsCommand(this.api, appId).withType('ios'),
                delete: (appId) => new DeleteVariantCommand_1.DeleteVariantCommand(this.api, appId).withType('ios'),
            },
            ios_token: {
                create: (appId) => new CreateIOSTokenVariantCommand_1.CreateIOSTokenVariantCommand(this.api, appId),
                update: (appId, variantId) => new UpdateIOSTokenVariantCommand_1.UpdateIOSTokenVariantCommand(this.api, appId, variantId),
                renewSecret: (appId, variantId) => new RenewSecretCommand_1.RenewSecretCommand(this.api, appId, 'ios_token', variantId),
                search: (appId) => new SearchVariantsCommand_1.SearchVariantsCommand(this.api, appId).withType('ios_token'),
                delete: (appId) => new DeleteVariantCommand_1.DeleteVariantCommand(this.api, appId).withType('ios_token'),
            },
            web_push: {
                create: (appId) => new CreateWebPushVariantCommand_1.CreateWebPushVariantCommand(this.api, appId),
                update: (appId, variantId) => new UpdateWebPushVariantCommand_1.UpdateWebPushVariantCommand(this.api, appId, variantId),
                renewSecret: (appId, variantId) => new RenewSecretCommand_1.RenewSecretCommand(this.api, appId, 'web_push', variantId),
                search: (appId) => new SearchVariantsCommand_1.SearchVariantsCommand(this.api, appId).withType('web_push'),
                delete: (appId) => new DeleteVariantCommand_1.DeleteVariantCommand(this.api, appId).withType('web_push'),
            },
        };
        this.config = {
            auth: {
                get: () => new GetConfigCommand_1.GetConfigCommand(this.api, 'auth'),
            },
            ui: {
                get: () => new GetConfigCommand_1.GetConfigCommand(this.api, 'ui'),
            },
        };
        this.apiURL = `${serverURL}/rest`;
        this.api = new ApiClient_1.ApiClient(this.apiURL, credentials);
        this.credentials = credentials;
    }
}
exports.UpsAdminClient = UpsAdminClient;
//# sourceMappingURL=UpsAdminClient.js.map